import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  clearSignupDecision,
  excludeSignupVisitor,
  SignupExperimentEvents,
  signupExperimentProperties,
} from '@/entities/user/signupExperiment';
import { assignTrialVariant } from '@/entities/user/trialVariant';

import { API } from '@/shared/api';
import { CONFLICT_STATUS_CODE } from '@/shared/api/errors';
import { CreateUserRequestData } from '@/shared/api/user/types';
import { isTrackingDisabled } from '@/shared/lib/isTrackingDisabled';
import { logout } from '@/shared/lib/logout';
import { isErrorGuard } from '@/shared/lib/types';
import { GTMEvents, setMixpanelUser, setMixpanelUserPropertiesOnce } from '@/shared/thirdParty';

import { LS_IS_JUST_SIGNED_IN_NAME } from '../lib/constants';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';

type CreateUserParams = {
  isSocial?: boolean;
  onError?: () => void;
};

export const useCreateUser = ({ isSocial = false }: CreateUserParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequestData) => API.createUser(data),
    onError: async (error) => {
      if (isErrorGuard(error, CONFLICT_STATUS_CODE)) {
        void logout();
        localStorage.clear();
        window.location.reload();
      }
    },
    onSuccess: async (newUser) => {
      setMixpanelUser(newUser.id, newUser.email, newUser.email_type);
      await assignTrialVariant({ queryClient, user: newUser });

      await queryClient.refetchQueries({ queryKey: USER_QUERY_KEYS.userData, type: 'active' });

      const excluded = isTrackingDisabled() || /@(?:brask|rask)\.ai$/i.test(newUser.email);
      if (excluded) excludeSignupVisitor();
      setMixpanelUserPropertiesOnce(excluded ? { signup_experiment_excluded: true } : signupExperimentProperties());
      SignupExperimentEvents.signUp({ isSocial });
      clearSignupDecision();
      GTMEvents.setUserId(newUser.id);
      try {
        await GTMEvents.identifyUser(newUser);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: 'adsTracking',
            phase: 'registrationIdentifyUser',
          },
        });
      }
      GTMEvents.registration(newUser.id);
      // remove this LS item to prevent sending 'login' event after 'registration'
      localStorage.removeItem(LS_IS_JUST_SIGNED_IN_NAME);
    },
  });
};
